import React from "react";
import { Exercise } from "../../models/Exercise";
import { Button, createStyles, darken, Grid, Theme, Tooltip, Typography } from "@material-ui/core";
import { format } from "date-fns";
import styled from "styled-components";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { blue, green, grey, orange } from "@material-ui/core/colors";
import { useSelector } from "react-redux";
import { State } from "../../store";
import getId from "../../utils/getId";
import { Gone } from "../../utils/exerciseSaleStatus";

interface IProps {
  exercise: Exercise;
  onBtnClick: (exercise: Exercise) => void;
}

const ExerciseButton = styled(Button)`
  display: inline;
  width: 100%;
  height: "3vw";
  border: "1px solid #fff";
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    grey: {
      backgroundColor: grey[300],
      color: theme.palette.getContrastText(grey[300]),
      "&:hover": {
        backgroundColor: darken(grey[300], 0.1),
      },
      borderRadius: 0,
      cursor: "not-allowed",
    },
    free: {
      backgroundColor: green[600],
      color: theme.palette.getContrastText(green[600]),
      "&:hover": {
        backgroundColor: darken(green[600], 0.1),
      },
      borderRadius: 0,
    },
    select: {
      backgroundColor: orange[800],
      color: theme.palette.getContrastText(orange[800]),
      "&:hover": {
        backgroundColor: darken(orange[800], 0.1),
      },
      borderRadius: 0,
    },
    notAvailable: {
      backgroundColor: blue[500],
      color: theme.palette.getContrastText(blue[500]),
      "&:hover": {
        backgroundColor: darken(blue[500], 0.1),
      },
      borderRadius: 0,
      cursor: "not-allowed",
    },
  })
);

const ExerciseSaleButtonSet: React.FC<IProps> = ({ exercise, onBtnClick }: IProps) => {
  const classes = useStyles();
  const selectExercise = useSelector((state: State) => state.basket.exerciseSelect);
  const isSelectExercise = selectExercise.some((item) => getId(item) === getId(exercise));
  const exerciseFree = exercise.capacity - exercise.reserved - exercise.prereservation;
  const exerciseDisabled = () => {
    if (exercise.status === Gone) {
      return false;
    }
    if (exerciseFree === 0) {
      return false;
    }
    if (exercise.saleVisibility === "N") {
      return false;
    }
    return true;
  };

  const exerciseBusy = () => {
    if (exerciseFree === 0) {
      return classes.grey;
    }
    if (exercise.saleVisibility === "N") {
      return classes.grey;
    }
    if (exercise.status === Gone) {
      return classes.grey;
    }
    if (isSelectExercise) {
      return classes.select;
    }
    return classes.free;
  };

  return (
    <Tooltip title={exerciseDisabled() ? "Wybierz zajęcie" : "Pozycja niedostępna"} placement="top">
      <ExerciseButton
        variant={"contained"}
        className={`${exerciseBusy()}`}
        onClick={exerciseDisabled() ? () => onBtnClick(exercise) : () => {}}
      >
        <Grid container alignItems={"center"}>
          <Grid item xs={6}>
            <Typography variant={"button"} display={"block"}>
              {format(exercise.startTime, "HH:mm")}
            </Typography>
            <Typography variant={"caption"} display={"block"}>
              {format(exercise.closeTime, "HH:mm")}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant={"caption"} display={"block"} style={{ margin: 0 }}>
              {exercise.capacity - (exercise.reserved + exercise.prereservation)}
            </Typography>
            <Typography variant={"caption"} display={"block"} style={{ margin: 0 }}>
              {exercise.reserved}
            </Typography>
            <Typography variant={"caption"} display={"block"} style={{ margin: 0 }}>
              {exercise.prereservation}
            </Typography>
          </Grid>
        </Grid>
      </ExerciseButton>
    </Tooltip>
  );
};

export default ExerciseSaleButtonSet;
